/**
 * SupportHubProvider — Unified context for the Support Hub widget
 *
 * Replaces FeedbackProvider as the top-level orchestrator.
 * Manages: panel open/close, active tab, search, FAB position,
 * error capture (inherited from original FeedbackProvider logic),
 * and the FeedbackCaptureOverlay bridge.
 */

import React, { useState, useEffect, useLayoutEffect, useRef, ReactNode } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { DraggableFab } from './DraggableFab';
import { SupportHubPanel } from './SupportHubPanel';
import { FeedbackCaptureOverlay } from '../admin-feedback/FeedbackCaptureOverlay';
import { HelpPageProvider } from '../help-chat/HelpPageContext';
import type { ChatMessage, EquipmentScope, FlowSuggestion } from '../help-chat/helpChat.types';
import type { FlowRun, FlowNode, FlowTerminal } from '../expert-chat/types';
import {
    SupportHubContext,
    type SupportHubTab,
    type FabPosition,
} from './SupportHubContext';

const getDefaultFabPosition = (): FabPosition => ({
    x: 16,
    y: typeof window !== 'undefined' ? window.innerHeight - 72 : 600,
    side: 'left',
});

const readStoredFabPosition = (): FabPosition => {
    if (typeof window === 'undefined') {
        return getDefaultFabPosition();
    }

    try {
        const stored = window.localStorage.getItem('support-hub-fab-position');
        if (!stored) {
            return getDefaultFabPosition();
        }

        const parsed = JSON.parse(stored) as Partial<FabPosition>;
        if (
            typeof parsed.x === 'number' &&
            typeof parsed.y === 'number' &&
            (parsed.side === 'left' || parsed.side === 'right')
        ) {
            return { x: parsed.x, y: parsed.y, side: parsed.side };
        }
    } catch {
        /* ignore */
    }

    return getDefaultFabPosition();
};

export const SupportHubProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<SupportHubTab>('home');
    const [searchQuery, setSearchQuery] = useState('');
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const [recentErrors, setRecentErrors] = useState<string[]>([]);
    const [contactPrefill, setContactPrefill] = useState<{ subject: string; message: string } | null>(null);
    const [pendingQuestion, setPendingQuestion] = useState<{ id?: string; question: string; contextId?: string | null } | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [expertSessionId, setExpertSessionId] = useState<string | null>(null);
    const [equipmentScope, setEquipmentScope] = useState<EquipmentScope | null>(null);
    const [flowOffers, setFlowOffers] = useState<FlowSuggestion[]>([]);
    const [dismissedFlowIds, setDismissedFlowIds] = useState<Set<string>>(() => new Set());
    const [activeFlowRun, setActiveFlowRun] = useState<FlowRun | null>(null);
    const [activeFlowNode, setActiveFlowNode] = useState<FlowNode | null>(null);
    const [activeFlowTerminal, setActiveFlowTerminal] = useState<FlowTerminal | null>(null);
    const [flowTitle, setFlowTitle] = useState<string | null>(null);
    const [flowError, setFlowError] = useState<string | null>(null);
    const [needsClarification, setNeedsClarification] = useState(false);
    const chatAbortControllerRef = useRef<AbortController | null>(null);
    const { user } = useAuth();

    const flowOffer = flowOffers.find((o) => !dismissedFlowIds.has(o.flow_id)) ?? null;

    const setFlowOffer = (offer: FlowSuggestion | null) => {
        if (!offer) {
            setFlowOffers([]);
        } else {
            setFlowOffers((prev) => {
                const next = prev.filter((o) => o.flow_id !== offer.flow_id);
                return [offer, ...next];
            });
        }
    };

    const consumeFlowOffer = (flowId: string) => {
        setFlowOffers((prev) => prev.filter((o) => o.flow_id !== flowId));
        setDismissedFlowIds((prev) => {
            const next = new Set(prev);
            next.add(flowId);
            return next;
        });
    };

    // The chat lives here (above the routes) so it survives tab switches, but sign-out is an
    // in-app navigation with no reload. Drop the conversation whenever the signed-in account
    // changes, before paint, so the next person on this browser never sees (or sends as
    // history) the previous account's chat.
    const userId = user?.id ?? null;
    const chatOwnerIdRef = useRef<string | null>(userId);
    const prevExpertSessionIdRef = useRef<string | null>(expertSessionId);

    useLayoutEffect(() => {
        if (prevExpertSessionIdRef.current !== expertSessionId) {
            prevExpertSessionIdRef.current = expertSessionId;
            setDismissedFlowIds(new Set());
        }
    }, [expertSessionId]);

    useLayoutEffect(() => {
        if (chatOwnerIdRef.current === userId) return;
        chatOwnerIdRef.current = userId;
        chatAbortControllerRef.current?.abort();
        chatAbortControllerRef.current = null;
        setMessages([]);
        setIsLoading(false);
        setPendingQuestion(null);
        setContactPrefill(null);
        setExpertSessionId(null);
        setEquipmentScope(null);
        setFlowOffers([]);
        setDismissedFlowIds(new Set());
        setActiveFlowRun(null);
        setActiveFlowNode(null);
        setActiveFlowTerminal(null);
        setFlowTitle(null);
        setFlowError(null);
        setNeedsClarification(false);
    }, [userId]);

    // FAB position — read from localStorage (DraggableFab manages writes)
    const [fabPosition, setFabPosition] = useState<FabPosition>(() => readStoredFabPosition());

    // Keep FAB position in sync without polling.
    useEffect(() => {
        const syncFabPosition = () => {
            const nextPosition = readStoredFabPosition();
            setFabPosition((currentPosition) => (
                currentPosition.x === nextPosition.x &&
                currentPosition.y === nextPosition.y &&
                currentPosition.side === nextPosition.side
            ) ? currentPosition : nextPosition);
        };

        const handleStorage = (event: StorageEvent) => {
            if (event.key === 'support-hub-fab-position') {
                syncFabPosition();
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                syncFabPosition();
            }
        };

        window.addEventListener('storage', handleStorage);
        window.addEventListener('focus', syncFabPosition);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('storage', handleStorage);
            window.removeEventListener('focus', syncFabPosition);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // ── Error capture (inherited from FeedbackProvider) ──

    const safeStringify = (obj: unknown) => {
        const cache = new Set();
        return JSON.stringify(obj, (_key, value) => {
            if (typeof value === 'object' && value !== null) {
                if (cache.has(value)) return '[Circular]';
                cache.add(value);
            }
            return value;
        });
    };

    useEffect(() => {
        const originalError = console.error;
        let isHandlingError = false;
        console.error = (...args) => {
            if (!isHandlingError) {
                isHandlingError = true;
                try {
                    const errorMsg = args.map(a => {
                        if (a instanceof Error) return a.stack || a.message;
                        try {
                            return typeof a === 'object' ? safeStringify(a) : String(a);
                        } catch {
                            return String(a);
                        }
                    }).join(' ');
                    setRecentErrors(prev => [...prev.slice(-9), errorMsg]);
                } catch {
                    // ignore
                } finally {
                    isHandlingError = false;
                }
            }
            originalError.apply(console, args);
        };

        const handleWindowError = (event: ErrorEvent) => {
            const errorMsg = `[Uncaught Error] ${event.message} at ${event.filename}:${event.lineno}`;
            setRecentErrors(prev => [...prev.slice(-9), errorMsg]);
        };

        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            const reason = event.reason;
            const errorMsg = `[Unhandled Promise Rejection] ${reason instanceof Error ? reason.stack || reason.message : typeof reason === 'object' ? safeStringify(reason) : String(reason)}`;
            setRecentErrors(prev => [...prev.slice(-9), errorMsg]);
        };

        window.addEventListener('error', handleWindowError);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        return () => {
            console.error = originalError;
            window.removeEventListener('error', handleWindowError);
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, []);

    // ── Environment flag gating ──
    const isFeedbackDisabled =
        (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_DISABLE_FEEDBACK_SYSTEM === 'true';
    if (isFeedbackDisabled) {
        return <>{children}</>;
    }

    // For unauthenticated users, never show the Support Hub
    if (!user) {
        return <>{children}</>;
    }

    // ── Panel controls ──

    const openPanel = (tab?: SupportHubTab) => {
        if (tab) setActiveTab(tab);
        setIsPanelOpen(true);
    };

    const closePanel = () => {
        setIsPanelOpen(false);
        setSearchQuery('');
    };

    const togglePanel = () => {
        if (isPanelOpen) closePanel();
        else openPanel();
    };

    const openOverlay = () => {
        setIsPanelOpen(false);
        setIsOverlayOpen(true);
    };

    const closeOverlay = () => setIsOverlayOpen(false);

    const clearContactPrefill = () => setContactPrefill(null);

    const askAiQuestion = (question: string, contextId?: string | null) => {
        setActiveTab('ai');
        setIsPanelOpen(true);
        const id = `pq-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        setPendingQuestion({ id, question, contextId });
    };

    return (
        <HelpPageProvider>
            <SupportHubContext.Provider
                value={{
                    isPanelOpen,
                    openPanel,
                    closePanel,
                    togglePanel,
                    activeTab,
                    setActiveTab,
                    searchQuery,
                    setSearchQuery,
                    fabPosition,
                    isOverlayOpen,
                    openOverlay,
                    closeOverlay,
                    recentErrors,
                    contactPrefill,
                    setContactPrefill,
                    clearContactPrefill,
                    pendingQuestion,
                    setPendingQuestion,
                    askAiQuestion,
                    messages,
                    setMessages,
                    isLoading,
                    setIsLoading,
                    chatAbortControllerRef,
                    expertSessionId,
                    setExpertSessionId,
                    equipmentScope,
                    setEquipmentScope,
                    flowOffer,
                    setFlowOffer,
                    flowOffers,
                    setFlowOffers,
                    dismissedFlowIds,
                    setDismissedFlowIds,
                    activeFlowRun,
                    setActiveFlowRun,
                    activeFlowNode,
                    setActiveFlowNode,
                    activeFlowTerminal,
                    setActiveFlowTerminal,
                    flowTitle,
                    setFlowTitle,
                    flowError,
                    setFlowError,
                    needsClarification,
                    setNeedsClarification,
                    consumeFlowOffer,
                }}
            >
                {children}

                <DraggableFab
                    onClick={togglePanel}
                    isPanelOpen={isPanelOpen}
                />
                <SupportHubPanel />
                <FeedbackCaptureOverlay />
            </SupportHubContext.Provider>
        </HelpPageProvider>
    );
};
